import React, { useEffect, useRef, useState } from "react";

import { FullbleedMediaBlock } from "@reactivated";

import { MediaChooserBlock } from "@thelabnyc/thelabui/src/components/MediaChooserBlock";

import { ButtonPrimary } from "../Clickables";
import { FeaturedRichtext } from "../FeaturedRichtext";
import { Svg } from "../Svg";

import styles from "./index.module.scss";

interface Props {
    value: FullbleedMediaBlock;
    /** When true, skips wrapper div and sticky positioning (for use inside other components). */
    controlled?: boolean;
    autoplay?: boolean;
}

export const FullbleedMedia = ({
    value,
    controlled = false,
    autoplay = true,
}: Props) => {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLElement>(null);
    const mediaContainerRef = useRef<HTMLDivElement>(null);
    const [isPlaying, setIsPlaying] = useState(true);

    useEffect(() => {
        // Skip internal scroll tracking if in controlled mode
        if (controlled) return;

        const handleScroll = () => {
            if (!wrapperRef.current || !containerRef.current) return;

            const rect = wrapperRef.current.getBoundingClientRect();
            const wrapperHeight = wrapperRef.current.offsetHeight;
            const viewportHeight = window.innerHeight;

            // Calculate how much of the wrapper has been scrolled through
            // Start when wrapper enters viewport, complete when it's about to leave
            const scrolled = -rect.top;
            const scrollableDistance = wrapperHeight - viewportHeight;

            // Progress from 0 to 1 as user scrolls through the wrapper
            const progress = Math.max(
                0,
                Math.min(1, scrolled / scrollableDistance),
            );

            containerRef.current.style.setProperty(
                "--effect-progress",
                `${progress}`,
            );
        };

        window.addEventListener("scroll", handleScroll, { passive: true });
        handleScroll();

        return () => {
            window.removeEventListener("scroll", handleScroll);
        };
    }, [controlled]);

    useEffect(() => {
        if (!mediaContainerRef.current) return;

        const video = mediaContainerRef.current.querySelector("video");
        if (video) {
            video.muted = true;
            video.playsInline = true;
            video.loop = true;
            video.controls = false;
            video.removeAttribute("controls");

            if (autoplay) {
                video.play().catch(() => {});
            }
        }
    }, [value.media, autoplay]);

    const hasContent = value.text || value.cta?.display_name;

    const hasVideo = value.media?.some((item) => item.type === "video");

    const handlePlayPause = () => {
        if (!mediaContainerRef.current) return;

        const video = mediaContainerRef.current.querySelector("video");
        if (video) {
            if (isPlaying) {
                video.pause();
            } else {
                video.play().catch(() => {});
            }
            setIsPlaying(!isPlaying);
        }
    };

    const content = (
        <section
            ref={containerRef}
            className={`${styles.root} ${controlled ? styles.controlled : styles.standalone}`}
        >
            <div ref={mediaContainerRef} className={styles.mediaContainer}>
                {value.media && <MediaChooserBlock value={value.media} />}
            </div>

            {/* Content - fades in with scroll */}
            {hasContent && (
                <div className={styles.content}>
                    {value.text && (
                        <div className={styles.text}>
                            <FeaturedRichtext value={value.text} />
                        </div>
                    )}
                    {value.cta?.display_name && (
                        <ButtonPrimary simpleLink={value.cta} />
                    )}
                </div>
            )}

            {/* Video play/pause control */}
            {hasVideo && autoplay && (
                <button
                    type="button"
                    className={styles.videoControl}
                    onClick={handlePlayPause}
                    aria-label={isPlaying ? "Pause video" : "Play video"}
                >
                    <Svg name={isPlaying ? "video/pause" : "video/play"} />
                </button>
            )}
        </section>
    );

    // In controlled mode, don't render wrapper
    if (controlled) {
        return content;
    }

    // In standalone mode, render with wrapper for scroll effect
    return (
        <div ref={wrapperRef} className={styles.wrapper}>
            {content}
        </div>
    );
};
